/** B2B invoice creation form (BP-09). Owner/manager only (route-guarded).
 *  Validates customer_tax_id non-empty + at least one line item, recomputes line
 *  totals client-side (the Edge Fn re-validates), and submits via createB2BInvoice.
 *  On success it shows the assigned invoice number. Trilingual via billing.b2b.* keys. */
import { Plus, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Field, IconButton, Input, Money, Select, Textarea } from '../../components/ui.js';

import { useCreateB2BInvoice } from './hooks/useB2BInvoices.js';

type TaxCode = 'A' | 'B' | 'C' | 'EXEMPT';
const TAX_CODES: TaxCode[] = ['A', 'B', 'C', 'EXEMPT'];

interface DraftLine {
  name: string;
  quantity: number;
  unit_price: number; // MKD minor units
  tax_rate_code: TaxCode;
}

function lineTotal(l: DraftLine): number {
  return Math.round(l.quantity * l.unit_price);
}

export function B2BInvoiceForm(): ReactNode {
  const { t } = useTranslation();
  const create = useCreateB2BInvoice();

  const [company, setCompany] = useState('');
  const [taxId, setTaxId] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([
    { name: '', quantity: 1, unit_price: 0, tax_rate_code: 'A' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);

  const total = lines.reduce((s, l) => s + lineTotal(l), 0);

  function setLine(i: number, patch: Partial<DraftLine>): void {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  async function submit(): Promise<void> {
    setError(null);
    setCreatedNumber(null);
    if (!company.trim()) {
      setError(t('billing.b2b.errors.company_required'));
      return;
    }
    if (!taxId.trim()) {
      setError(t('billing.b2b.errors.tax_id_required'));
      return;
    }
    const validLines = lines.filter((l) => l.name.trim() && l.quantity > 0 && l.unit_price > 0);
    if (validLines.length === 0) {
      setError(t('billing.b2b.errors.line_items_required'));
      return;
    }
    try {
      const res = await create.mutateAsync({
        customer_company: company.trim(),
        customer_tax_id: taxId.trim(),
        ...(address.trim() ? { customer_address: address.trim() } : {}),
        ...(email.trim() ? { customer_email: email.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        line_items: validLines.map((l) => ({
          name: l.name.trim(),
          quantity: l.quantity,
          unit_price: l.unit_price,
          tax_rate_code: l.tax_rate_code,
          total: lineTotal(l),
        })),
      });
      if (res.fiscal_status === 'failed_retriable') {
        setError(t('billing.b2b.fiscalRetriable'));
        return;
      }
      setCreatedNumber(res.invoice_number);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div>
      <h3 className="mb-4 font-display text-lg font-semibold text-fg">
        {t('billing.b2b.createTitle')}
      </h3>
      <div className="grid gap-3">
        <Field label={t('billing.b2b.customerCompany')} required>
          {(id) => <Input id={id} value={company} onChange={(e) => setCompany(e.target.value)} />}
        </Field>
        <Field label={t('billing.b2b.customerTaxId')} required>
          {(id) => <Input id={id} value={taxId} onChange={(e) => setTaxId(e.target.value)} />}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('billing.b2b.customerAddress')}>
            {(id) => <Input id={id} value={address} onChange={(e) => setAddress(e.target.value)} />}
          </Field>
          <Field label={t('billing.b2b.customerEmail')}>
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="mt-1">
          <p className="mb-1.5 text-sm font-medium text-muted">{t('billing.b2b.lineItems')}</p>
          <div className="grid gap-2">
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  placeholder={t('billing.b2b.itemName')}
                  value={l.name}
                  onChange={(e) => setLine(i, { name: e.target.value })}
                  className="flex-[2]"
                />
                <Input
                  type="number"
                  min={1}
                  value={l.quantity}
                  onChange={(e) => setLine(i, { quantity: Number(e.target.value) })}
                  className="w-16"
                  aria-label={t('billing.b2b.quantity')}
                />
                <Input
                  type="number"
                  min={0}
                  value={l.unit_price}
                  onChange={(e) => setLine(i, { unit_price: Number(e.target.value) })}
                  className="w-24"
                  aria-label={t('billing.b2b.unitPrice')}
                />
                <Select
                  value={l.tax_rate_code}
                  onChange={(e) => setLine(i, { tax_rate_code: e.target.value as TaxCode })}
                  aria-label={t('billing.b2b.taxCode')}
                  className="w-20"
                >
                  {TAX_CODES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
                {lines.length > 1 ? (
                  <IconButton
                    label={t('common.remove')}
                    className="h-9 w-9 shrink-0"
                    onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                ) : null}
              </div>
            ))}
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="mt-2"
            icon={<Plus className="h-4 w-4" />}
            onClick={() =>
              setLines((prev) => [
                ...prev,
                { name: '', quantity: 1, unit_price: 0, tax_rate_code: 'A' },
              ])
            }
          >
            {t('billing.b2b.addLine')}
          </Button>
        </div>

        <Field label={t('billing.b2b.notes')}>
          {(id) => (
            <Textarea id={id} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          )}
        </Field>

        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="text-sm font-medium text-muted">{t('billing.b2b.total')}</span>
          <Money minor={total} className="text-lg font-bold" />
        </div>

        {error ? <p className="text-sm font-medium text-danger">{error}</p> : null}
        {createdNumber ? (
          <p className="text-sm font-medium text-success">
            {t('billing.b2b.created', { number: createdNumber })}
          </p>
        ) : null}

        <Button onClick={() => void submit()} loading={create.isPending}>
          {t('billing.b2b.submit')}
        </Button>
      </div>
    </div>
  );
}
